import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface CustomsDeclarationDialogProps {
  data: any
  onSave?: (updatedData: any, changes?: any[]) => void
  shipmentId: string
  documentId: string
}

const customsDeclarationSections: DocumentSection[] = [
  {
    id: 'documentInfo',
    title: 'Document Information',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Declaration Number', path: 'declarationNumber' },
      { type: 'field', label: 'Declaration Type', path: 'declarationType' },
      { type: 'field', label: 'Customs Procedure', path: 'customsProcedure' },
      { type: 'field', label: 'Declaration Date', path: 'declarationDate' },
    ],
  },
  {
    id: 'referenceNumbers',
    title: 'Reference Numbers',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Reference Numbers',
        path: 'referenceNumbers',
        fields: [
          { label: 'Bill of Lading Number', path: 'billOfLadingNumber' },
          { label: 'Invoice Number', path: 'invoiceNumber' },
          { label: 'Purchase Order Number', path: 'purchaseOrderNumber' },
          { label: 'Shipment Reference Number', path: 'shipmentReferenceNumber' },
        ],
      },
    ],
  },
  {
    id: 'parties',
    title: 'Parties',
    layout: 'single',
    fields: [
      {
        type: 'object',
        label: 'Declarant',
        path: 'declarant',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
      {
        type: 'object',
        label: 'Importer',
        path: 'importer',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
      {
        type: 'object',
        label: 'Exporter',
        path: 'exporter',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
      {
        type: 'object',
        label: 'Carrier',
        path: 'carrier',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
      {
        type: 'object',
        label: 'Agent',
        path: 'agent',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
      {
        type: 'object',
        label: 'Consignee',
        path: 'consignee',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customs ID', path: 'customsId' },
        ],
      },
    ],
  },
  {
    id: 'transportDetails',
    title: 'Transport Details',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Transport Mode', path: 'transportMode' },
      {
        type: 'object',
        label: 'Transport Details',
        path: 'transportDetails',
        fields: [
          { label: 'Vessel Name', path: 'vesselName' },
          { label: 'Flight Number', path: 'flightNumber' },
          { label: 'Vehicle Registration', path: 'vehicleRegistration' },
        ],
      },
      {
        type: 'array',
        label: 'Container Numbers',
        path: 'transportDetails.containerNumbers',
        columns: [{ label: 'Container Number', path: '.' }],
      },
    ],
  },
  {
    id: 'locations',
    title: 'Locations',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Office of Entry', path: 'officeOfEntry' },
      { type: 'field', label: 'Office of Exit', path: 'officeOfExit' },
      { type: 'field', label: 'Port of Loading', path: 'portOfLoading' },
      { type: 'field', label: 'Port of Discharge', path: 'portOfDischarge' },
      { type: 'field', label: 'Final Destination', path: 'finalDestination' },
    ],
  },
  {
    id: 'dates',
    title: 'Important Dates',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Arrival Date', path: 'arrivalDate' },
      { type: 'field', label: 'Departure Date', path: 'departureDate' },
    ],
  },
  {
    id: 'financialInfo',
    title: 'Financial Information',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Total Customs Value', path: 'totalCustomsValue', complexType: 'currency' },
      { type: 'field', label: 'Total Invoice Value', path: 'totalInvoiceValue', complexType: 'currency' },
      { type: 'field', label: 'Freight Cost', path: 'freightCost', complexType: 'currency' },
      { type: 'field', label: 'Insurance Cost', path: 'insuranceCost', complexType: 'currency' },
      { type: 'field', label: 'Other Costs', path: 'otherCosts', complexType: 'currency' },
      { type: 'field', label: 'Currency', path: 'currency' },
      { type: 'field', label: 'Incoterm', path: 'incoterm' },
    ],
  },
  {
    id: 'goodsSummary',
    title: 'Goods Summary',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Total Packages', path: 'totalPackages' },
      { type: 'field', label: 'Total Gross Weight', path: 'totalGrossWeight', complexType: 'weight' },
      { type: 'field', label: 'Total Net Weight', path: 'totalNetWeight', complexType: 'weight' },
    ],
  },
  {
    id: 'goods',
    title: 'Goods Details',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Goods',
        path: 'goods',
        columns: [
          { label: 'Description', path: 'description' },
          { label: 'HS Code', path: 'hsCode' },
          { label: 'Country of Origin', path: 'countryOfOrigin' },
          { label: 'Quantity', path: 'quantity' },
          { label: 'Quantity Unit', path: 'quantityUnit' },
          { label: 'Weight', path: 'weight', complexType: 'weight' },
          { label: 'Volume', path: 'volume', complexType: 'volume' },
          { label: 'Value', path: 'value', complexType: 'currency' },
          { label: 'Package Type', path: 'packageType' },
          { label: 'Package Count', path: 'packageCount' },
          { label: 'Marks & Numbers', path: 'marksAndNumbers' },
          { label: 'Additional Info', path: 'additionalInformation' },
        ],
      },
    ],
  },
  {
    id: 'duties',
    title: 'Duties',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Duties',
        path: 'duties',
        columns: [
          { label: 'Duty Type', path: 'dutyType' },
          { label: 'Taxable Base', path: 'taxableBase', complexType: 'currency' },
          { label: 'Rate (%)', path: 'rate' },
          { label: 'Amount', path: 'amount', complexType: 'currency' },
        ],
      },
    ],
  },
  {
    id: 'taxes',
    title: 'Taxes',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Taxes',
        path: 'taxes',
        columns: [
          { label: 'Tax Type', path: 'taxType' },
          { label: 'Taxable Base', path: 'taxableBase', complexType: 'currency' },
          { label: 'Rate (%)', path: 'rate' },
          { label: 'Amount', path: 'amount', complexType: 'currency' },
        ],
      },
    ],
  },
  {
    id: 'totalAmounts',
    title: 'Total Amounts',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Total Duty Amount', path: 'totalDutyAmount', complexType: 'currency' },
      { type: 'field', label: 'Total Tax Amount', path: 'totalTaxAmount', complexType: 'currency' },
      { type: 'field', label: 'Total Payable', path: 'totalPayable', complexType: 'currency' },
    ],
  },
  {
    id: 'documents',
    title: 'Supporting Documents',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Documents',
        path: 'documents',
        columns: [
          { label: 'Document Type', path: 'documentType' },
          { label: 'Document Number', path: 'documentNumber' },
          { label: 'Issue Date', path: 'issueDate' },
          { label: 'Issuing Authority', path: 'issuingAuthority' },
        ],
      },
    ],
  },
  {
    id: 'additionalInfo',
    title: 'Additional Information',
    layout: 'single',
    fields: [
      { type: 'field', label: 'Remarks', path: 'remarks', fullWidth: true },
      { type: 'field', label: 'Special Instructions', path: 'specialInstructions', fullWidth: true },
    ],
  },
  {
    id: 'officialUse',
    title: 'Official Use',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Official Use',
        path: 'officialUse',
        fields: [
          { label: 'Entry Number', path: 'entryNumber' },
          { label: 'Processing Date', path: 'processingDate' },
          { label: 'Officer Name', path: 'officerName' },
          { label: 'Officer Badge', path: 'officerBadge' },
          { label: 'Clearance Status', path: 'clearanceStatus' },
          { label: 'Clearance Date', path: 'clearanceDate' },
        ],
      },
    ],
  },
]

const CustomsDeclarationDialog: React.FC<CustomsDeclarationDialogProps> = ({
  data,
  onSave,
  shipmentId,
  documentId,
}) => {
  return (
    <DocumentDialog
      documentData={data}
      schema={{}} // TODO: Import customsDeclarationSchema from backend
      sections={customsDeclarationSections}
      documentType="Customs Declaration"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default CustomsDeclarationDialog
